import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { LearnerViewInPlay } from "@pushback-and-chaos/contracts";
import { clampDragDelta, usePointerDrag, type ClientRect } from "../../ui/use-drag.js";
import { useDocumentPlan, type DeskDocument } from "../../documents/desk.js";
import { PageAtoms, type PageItem } from "./pages.js";
import { documentItems, useColumnGroups } from "./document-pages.js";

/**
 * A PAPER, PICKED UP.
 *
 * A document opens as a window you can move, over the conversation OR over the
 * scene — the two panels you read a document against. It is the live design's
 * own behaviour, re-sited: the desk is on the right now, so "picked up" means
 * carried off the desk in any direction rather than dragged across one rule.
 *
 * Three things this keeps that a panel-filling reader could not:
 *
 *   - the learner decides where the paper sits, and it stays there
 *   - two papers can be laid side by side and compared
 *   - the desk keeps its cards while a paper is in hand
 *
 * THE WINDOW SCROLLS (2026-10-02). A document is one sheet: its height is its
 * content's until the cap at 74% of the play surface, and past that the sheet
 * scrolls inside the window. Nothing is cut, a table is as tall as it is, and
 * a wide table is still cut into COLUMN GROUPS (document-pages.tsx), because
 * sideways is the one direction a paper must not scroll — a figure read
 * against the wrong column is worse than a figure read on the next line.
 *
 * Position is a FRACTION of the play surface's free range, not a pixel offset.
 * A pixel offset saved on a 27" display reopens a document off-screen on a
 * laptop; a fraction cannot, at any size, by construction.
 */

/** Where a window sits: fractions of the play surface's free range, both in [0, 1]. */
export interface WindowPlace {
  readonly fx: number;
  readonly fy: number;
}

/**
 * How wide a paper is, and how tall it may get, as a share of the play
 * surface.
 *
 * A READING width for prose — a memo, a transcript, a note — because a line
 * of forty-odd characters too many is a line nobody finishes.
 *
 * A TABLE width for figures, because the thing a table is FOR is comparing
 * one row across every column, and cutting it into column groups breaks
 * exactly that. The groups still exist and still work — a sheet with fourteen
 * months in it will not fit on any screen — but a real case document has four
 * or five columns, and at this width it opens whole.
 */
const WIDTH = "min(52ch, 90%)";
const TABLE_WIDTH = "min(80ch, 94%)";
const MAX_HEIGHT = "74%";

/** The play surface a window moves in. */
export interface SurfaceSize {
  readonly width: number;
  readonly height: number;
}

function unit(value: number): number {
  return Math.min(Math.max(Number.isFinite(value) ? value : 0, 0), 1);
}

function round(value: number): number {
  return Math.round(value * 10000) / 10000;
}

/** A point on the play surface -> a place, given the room a window of this size has to move in. */
export function placeInSurface(surface: ClientRect, size: { readonly width: number; readonly height: number }, left: number, top: number): WindowPlace {
  const freeX = Math.max(surface.right - surface.left - size.width, 0);
  const freeY = Math.max(surface.bottom - surface.top - size.height, 0);
  return {
    fx: round(unit(freeX > 0 ? (left - surface.left) / freeX : 0)),
    fy: round(unit(freeY > 0 ? (top - surface.top) / freeY : 0)),
  };
}

/** The default corner for a paper opened without a gesture — stepped, so a second one is not hidden by the first. */
export function defaultPlace(index: number): WindowPlace {
  return { fx: round(unit(0.06 + index * 0.05)), fy: round(unit(0.1 + index * 0.06)) };
}

/**
 * One window. `kind` decides what is inside it; everything about being a
 * window — where it is, how it moves, how it scrolls, how it is put down —
 * is the same for a document and for the learner's own notes.
 */
export function PaperWindow({
  title,
  place,
  surface,
  zIndex,
  tint,
  width = WIDTH,
  kind,
  items,
  onMeasuredContent,
  onFront,
  onMove,
  onToDesk,
  onClose,
  what,
}: {
  readonly title: string;
  readonly place: WindowPlace;
  /** The play surface's own size, so a fraction can be resolved to a corner. */
  readonly surface: SurfaceSize;
  readonly zIndex: number;
  readonly tint?: { readonly fill: string; readonly edge: string } | undefined;
  /** How wide this paper is. Prose reads narrow; figures need the table's room. */
  readonly width?: string | undefined;
  /** What kind of paper this is, for styling — never rendered (Law 2). */
  readonly kind?: string | undefined;
  readonly items: readonly PageItem[];
  /** The sheet as laid out, after every change of content or width: how a wide table is found out. */
  readonly onMeasuredContent?: ((root: HTMLElement, width: number) => void) | undefined;
  readonly onFront: () => void;
  readonly onMove: (place: WindowPlace) => void;
  /** Dropped back on the desk: put it down there, at this point. */
  readonly onToDesk: (clientX: number, clientY: number) => void;
  readonly onClose: () => void;
  readonly what: string;
}): ReactNode {
  const ref = useRef<HTMLDivElement | null>(null);
  const body = useRef<HTMLDivElement | null>(null);
  const [delta, setDelta] = useState<{ x: number; y: number } | null>(null);
  /** Where the window and the play surface were when the press began — the walls are measured once per drag. */
  const origin = useRef<{ window: ClientRect; surface: ClientRect } | null>(null);
  /** The window's own size, so its fraction resolves to a corner that is really on the surface. */
  const [size, setSize] = useState<SurfaceSize>({ width: 0, height: 0 });

  useEffect(() => {
    const element = ref.current;
    if (element === null || typeof ResizeObserver === "undefined") return;
    const read = (): void => {
      const rect = element.getBoundingClientRect();
      setSize((previous) => (Math.abs(previous.width - rect.width) < 0.5 && Math.abs(previous.height - rect.height) < 0.5 ? previous : { width: rect.width, height: rect.height }));
    };
    read();
    const observer = new ResizeObserver(read);
    observer.observe(element);
    return () => { observer.disconnect(); };
  }, []);

  /*
   * THE SHEET, MEASURED IN PLACE. The column-group decision needs the table's
   * natural widths at the width the sheet really has, and a scrolling body
   * shows the whole sheet, so it is asked directly — after every render that
   * changed the content, and again when the window's width changes.
   */
  const measured = useRef(onMeasuredContent);
  measured.current = onMeasuredContent;
  useLayoutEffect(() => {
    const element = body.current;
    if (element !== null) measured.current?.(element, element.clientWidth);
  }, [items]);
  useEffect(() => {
    const element = body.current;
    if (element === null || typeof ResizeObserver === "undefined") return;
    let width = element.clientWidth;
    const observer = new ResizeObserver(() => {
      if (Math.abs(element.clientWidth - width) < 0.5) return;
      width = element.clientWidth;
      measured.current?.(element, width);
    });
    observer.observe(element);
    return () => { observer.disconnect(); };
  }, []);

  const walled = (dx: number, dy: number): { x: number; y: number } => {
    const o = origin.current;
    if (o === null) return { x: dx, y: dy };
    return clampDragDelta(o.window, o.surface, { width: globalThis.innerWidth, height: globalThis.innerHeight }, dx, dy);
  };

  const handle = usePointerDrag({
    onStart: () => {
      onFront();
      const element = ref.current;
      const surface = element?.offsetParent;
      origin.current =
        element !== null && element !== undefined && surface instanceof HTMLElement
          ? { window: element.getBoundingClientRect(), surface: surface.getBoundingClientRect() }
          : null;
    },
    onMove: (dx, dy) => { setDelta(walled(dx, dy)); },
    onEnd: (end) => {
      setDelta(null);
      if (!end.moved) return;
      const o = origin.current;
      const moved = walled(end.dx, end.dy);
      // Back onto the desk: the paper becomes a card again, where it was let
      // go. Tested against the desk's own rectangle, not against an edge.
      const desk = ref.current?.ownerDocument.querySelector('[data-region="desk"]')?.getBoundingClientRect();
      if (desk !== undefined && end.clientX >= desk.left && end.clientX <= desk.right && end.clientY >= desk.top && end.clientY <= desk.bottom) {
        onToDesk(end.clientX, end.clientY);
        return;
      }
      if (o === null) return;
      // Where it STOPPED, never re-derived from the pointer: re-deriving is
      // what used to make a card jump the moment it was let go.
      onMove(placeInSurface(o.surface, { width: o.window.right - o.window.left, height: o.window.bottom - o.window.top }, o.window.left + moved.x, o.window.top + moved.y));
    },
  });

  return (
    <div
      ref={ref}
      className="pcs-window"
      data-paper-window={what}
      data-paper-kind={kind}
      style={{
        // The fraction resolved against the room this window actually has to
        // move in — the surface less the window's own measured size. Zero
        // before the first measurement, which puts a new window at the corner
        // its fraction names and never off the surface.
        left: `${String(Math.round(place.fx * Math.max(surface.width - size.width, 0)))}px`,
        top: `${String(Math.round(place.fy * Math.max(surface.height - size.height, 0)))}px`,
        width,
        maxHeight: MAX_HEIGHT,
        zIndex: delta === null ? zIndex : 900,
        transform: delta === null ? undefined : `translate(${String(delta.x)}px, ${String(delta.y)}px)`,
        ...(tint === undefined ? {} : { background: tint.fill, borderColor: tint.edge }),
      }}
    >
      <header className="pcs-window-bar" onPointerDown={handle}>
        <span className="pcs-window-title" title={title}>
          {title}
        </span>
        <button type="button" onClick={onClose} aria-label="Put this down">
          put it down.
        </button>
      </header>
      {/* The sheet. As tall as its content up to the window's cap, and
          scrolling past it: a forty-word post-it is a post-it, and a board
          pack is read down. */}
      <div ref={body} className="pcs-window-body pcs-doc-body pc-scroll" data-region="page" aria-label={title}>
        <PageAtoms items={items} />
      </div>
    </div>
  );
}

/**
 * A document in a window. The render plan is fetched HERE and only here,
 * which is what makes the fetch mean "the learner opened this" (ADR 0008).
 * Moving the window, scrolling it and putting it down fetch nothing.
 */
export function DocumentWindow({
  sessionId,
  card,
  place,
  surface,
  zIndex,
  onFront,
  onMove,
  onToDesk,
  onClose,
}: {
  readonly sessionId: string;
  readonly card: DeskDocument;
  readonly place: WindowPlace;
  readonly surface: SurfaceSize;
  readonly zIndex: number;
  readonly onFront: () => void;
  readonly onMove: (place: WindowPlace) => void;
  readonly onToDesk: (clientX: number, clientY: number) => void;
  readonly onClose: () => void;
}): ReactNode {
  const plan = useDocumentPlan(sessionId, card.id);
  // The key the column decision is reopened on: until the plan lands there is
  // nothing on the sheet but a holding line, and a decision made against that
  // would stand for the whole document.
  const { groups, onMeasured } = useColumnGroups(plan === null ? "" : `${card.id}:${String(plan.blocks.length)}`);
  /*
   * FIGURES OPEN WIDE.
   *
   * The kind decides it, not the content, so a figures paper is this wide
   * from its first frame and never jumps a moment later when its plan lands
   * — and because it is the kind, it is predictable: a figures card always
   * opens the same shape. A paper of any OTHER kind that turns out to carry
   * a table gets the same room once its plan is in, because withholding it
   * from a memo's sheet would be the same fault wearing a different suit.
   */
  const carriesTable = plan !== null && plan.blocks.some((block) => block.role === "table" || block.role === "sheet-table");
  const figures = card.kind === "FINANCIAL_TABLE";
  const items = useMemo<readonly PageItem[]>(
    () => (plan === null ? [{ key: "reading", kind: "text", text: "Reading it.", className: "pcs-quiet" }] : documentItems(plan, groups)),
    [plan, groups],
  );
  const measured = useCallback((root: HTMLElement, width: number) => { onMeasured(root, width); }, [onMeasured]);
  return (
    <PaperWindow
      title={plan?.card.title ?? card.title}
      place={place}
      surface={surface}
      zIndex={zIndex}
      width={figures || carriesTable ? TABLE_WIDTH : WIDTH}
      // A figures paper is marked, quietly, with the edge the design system
      // already gives its kind — enough that the wider shape reads as a class
      // of document rather than as a window that happens to be bigger.
      tint={figures ? { fill: "var(--pcs-card-paper)", edge: "var(--pc-doc-financial-edge)" } : undefined}
      kind={figures ? "figures" : carriesTable ? "figures-bearing" : "prose"}
      items={items}
      onMeasuredContent={measured}
      onFront={onFront}
      onMove={onMove}
      onToDesk={onToDesk}
      onClose={onClose}
      what="document"
    />
  );
}

/**
 * The learner's own notes, in a window like any other paper — established
 * findings in the blue of a pen, open questions in the grey of a pencil.
 */
export function NotesWindow({
  notes,
  hypotheses,
  threads,
  place,
  surface,
  zIndex,
  onFront,
  onMove,
  onToDesk,
  onClose,
}: {
  readonly notes: { readonly established: readonly string[]; readonly open: readonly string[] };
  readonly hypotheses: readonly string[];
  readonly threads: LearnerViewInPlay["open_threads"];
  readonly place: WindowPlace;
  readonly surface: SurfaceSize;
  readonly zIndex: number;
  readonly onFront: () => void;
  readonly onMove: (place: WindowPlace) => void;
  readonly onToDesk: (clientX: number, clientY: number) => void;
  readonly onClose: () => void;
}): ReactNode {
  const items = useMemo<readonly PageItem[]>(() => {
    const built: PageItem[] = [];
    if (hypotheses.length === 0 && threads.length === 0 && notes.established.length === 0 && notes.open.length === 0) {
      built.push({ key: "none", kind: "text", text: "Nothing on record yet.", className: "pcs-doc-note" });
    }
    for (const [index, line] of notes.established.entries()) built.push({ key: `e${String(index)}`, kind: "text", text: line, className: "pcs-doc-note" });
    for (const [index, line] of hypotheses.entries()) built.push({ key: `h${String(index)}`, kind: "text", text: line, className: "pcs-doc-note" });
    for (const [index, line] of notes.open.entries()) built.push({ key: `o${String(index)}`, kind: "text", text: line, className: "pcs-doc-note pcs-doc-note-open" });
    for (const [index, thread] of threads.entries()) built.push({ key: `t${String(index)}`, kind: "text", text: String(thread), className: "pcs-doc-p" });
    return built;
  }, [notes, hypotheses, threads]);

  return (
    <PaperWindow
      title="In your own notes."
      place={place}
      surface={surface}
      zIndex={zIndex}
      tint={{ fill: "var(--pc-doc-postit-fill)", edge: "var(--pc-doc-postit-edge)" }}
      items={items}
      onFront={onFront}
      onMove={onMove}
      onToDesk={onToDesk}
      onClose={onClose}
      what="notes"
    />
  );
}
